import React from 'react';

const InventoryPrint = ({ inventory, selectedCategory, selectedProduct, selectedMonth }) => {
  if (!inventory) return null;

  const formatCurrency = (value) => {
    if (!value) return '0';
    return `${parseFloat(value).toFixed(2)}`;
  };

  const formatQuantity = (value) => {
    if (!value) return '0';
    return parseFloat(value).toFixed(2);
  };

  const formatMonthForDisplay = (yearMonth) => {
    if (!yearMonth) return "";
    const [year, month] = yearMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Extract numeric value from string (removes 'kg', 'm', etc)
  const extractNumericValue = (str) => {
    if (!str) return 0;
    if (typeof str === 'number') return str;
    const numeric = parseFloat(str.replace(/[^0-9.]/g, ''));
    return isNaN(numeric) ? 0 : numeric;
  };

  // Check category type
  const isReelOrTota = (categoryKey) => {
    const lower = categoryKey.toLowerCase();
    return lower === 'reel' || lower === 'tota';
  };

  const isRoll = (categoryKey) => {
    return categoryKey.toLowerCase() === 'roll';
  };

  const isPacket = (categoryKey) => {
    return categoryKey.toLowerCase() === 'packet';
  };

  // Calculate pieces from weight for reel/tota
  const calculatePieces = (totalWeight, weightPerPiece) => {
    if (!totalWeight || !weightPerPiece) return 0;
    const weight = parseFloat(weightPerPiece.replace(/[^0-9.]/g, ''));
    if (weight === 0) return 0;
    return totalWeight / weight;
  };

  // Calculate category totals
  const calculateCategoryTotals = (categoryKey, categoryData) => {
    let totalQuantity = 0;
    let totalValue = 0;
    
    const lowerCategory = categoryKey.toLowerCase();
    
    if (isReelOrTota(lowerCategory)) {
      Object.values(categoryData).forEach(productData => {
        Object.values(productData).forEach(weightData => {
          Object.values(weightData).forEach(lengthData => {
            Object.values(lengthData).forEach(vendorData => {
              totalQuantity += parseFloat(vendorData.quantity) || 0;
              totalValue += parseFloat(vendorData.totalPrice) || 0;
            });
          });
        });
      });
    } 
    else if (isRoll(lowerCategory)) {
      Object.values(categoryData).forEach(productData => {
        Object.values(productData).forEach(sizeData => {
          if (sizeData.quantity !== undefined) {
            totalQuantity += parseFloat(sizeData.quantity) || 0;
            totalValue += parseFloat(sizeData.totalPrice) || 0;
          } else {
            Object.values(sizeData).forEach(vendorData => {
              totalQuantity += parseFloat(vendorData.quantity) || 0;
              totalValue += parseFloat(vendorData.totalPrice) || 0;
            });
          }
        });
      });
    }
    else if (isPacket(lowerCategory)) {
      Object.values(categoryData).forEach(productData => {
        Object.values(productData).forEach(variantData => {
          totalQuantity += parseFloat(variantData.quantity) || 0;
          totalValue += parseFloat(variantData.totalPrice) || 0;
        });
      });
    }
    else {
      Object.values(categoryData).forEach(productData => {
        totalQuantity += parseFloat(productData.quantity) || 0;
        totalValue += parseFloat(productData.totalPrice) || 0;
      });
    }
    
    return { totalQuantity, totalValue };
  };

  let grandTotalQuantity = 0;
  let grandTotalValue = 0;

  // Get category order
  const categoryOrder = ['packet', 'reel', 'roll', 'tota'];
  
  // Sort categories based on order
  const sortedCategories = Object.keys(inventory).sort((a, b) => {
    const indexA = categoryOrder.indexOf(a.toLowerCase());
    const indexB = categoryOrder.indexOf(b.toLowerCase());
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '5px' }}>Full Inventory</h1>
      
      {sortedCategories.map((categoryKey) => {
        const categoryData = inventory[categoryKey];
        const categoryTotals = calculateCategoryTotals(categoryKey, categoryData);
        grandTotalQuantity += categoryTotals.totalQuantity;
        grandTotalValue += categoryTotals.totalValue;
        
        const isReelTota = isReelOrTota(categoryKey);
        const isRollCat = isRoll(categoryKey);
        const isPacketCat = isPacket(categoryKey);
        
        return (
          <div key={categoryKey} style={{ marginBottom: '30px' }}>
            <h2 style={{ 
              backgroundColor: '#0a8938', 
              color: 'white', 
              padding: '8px 12px', 
              borderRadius: '4px',
              marginBottom: '10px',
              fontSize: '16px',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>{categoryKey.toUpperCase()}</span>
              <span>Total: {formatQuantity(categoryTotals.totalQuantity)} units | {formatCurrency(categoryTotals.totalValue)}</span>
            </h2>
            
            {/* ========== PACKET ========== */}
            {isPacketCat && (
              Object.entries(categoryData).map(([productName, variants]) => {
                // Sort variants by size, ktae, gram
                const sortedVariants = Object.entries(variants).sort((a, b) => {
                  const aParts = a[0].split('|');
                  const bParts = b[0].split('|');
                  const aSize = extractNumericValue(aParts[0]);
                  const bSize = extractNumericValue(bParts[0]);
                  if (aSize !== bSize) return aSize - bSize;
                  const aKtae = parseFloat(aParts[1]) || 0;
                  const bKtae = parseFloat(bParts[1]) || 0;
                  if (aKtae !== bKtae) return aKtae - bKtae;
                  const aGram = parseFloat(aParts[2]) || 0;
                  const bGram = parseFloat(bParts[2]) || 0;
                  return aGram - bGram;
                });
                
                return (
                  <div key={productName} style={{ marginBottom: '20px' }}>
                    <h3 style={{ backgroundColor: '#e8f5e9', padding: '6px 10px', marginBottom: '5px', fontWeight: 'normal' }}>
                      {productName}
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Size</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Ktae</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Gram</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Qty</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Rate</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedVariants.map(([variantKey, variantData]) => {
                          const [size, ktae, gram] = variantKey.split('|');
                          return (
                            <tr key={variantKey}>
                              <td style={{ border: '1px solid #ddd', padding: '6px' }}>{size.trim()}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px' }}>{ktae.trim()}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px' }}>{gram.trim()}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatQuantity(variantData.quantity)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(variantData.rate)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(variantData.totalPrice)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
  <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
    <td colSpan={3} style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      Total for {productName}:
    </td>
    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      {formatQuantity(
        sortedVariants.reduce((sum, [, v]) => sum + (parseFloat(v.quantity) || 0), 0)
      )}
    </td>
    <td style={{ border: '1px solid #ddd', padding: '6px' }}></td>
    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      {formatCurrency(
        sortedVariants.reduce((sum, [, v]) => sum + (parseFloat(v.totalPrice) || 0), 0)
      )}
    </td>
  </tr>
</tfoot>

                    </table>
                  </div>
                );
              })
            )}
            
            {/* ========== REEL / TOTA ========== */}
            {isReelTota && (
              Object.entries(categoryData).map(([productName, productData]) => {
                // Build all entries with their keys for sorting
                const entries = [];
                Object.entries(productData).forEach(([weightKey, weightData]) => {
                  Object.entries(weightData).forEach(([sizeKey, sizeData]) => {
                    Object.entries(sizeData).forEach(([vendorName, vendorData]) => {
                      // Calculate pieces: totalWeight / weightPerPiece
                      const totalWeight = parseFloat(vendorData.quantity) || 0;
                      const weightPerPiece = extractNumericValue(weightKey);
                      const pieces = weightPerPiece > 0 ? totalWeight / weightPerPiece : 0;
                      
                      entries.push({
                        weightKey,
                        sizeKey,
                        vendorName,
                        vendorData,
                        pieces // Store the calculated pieces
                      });
                    });
                  });
                });
                
                // Sort by: Size (numeric), Weight (numeric), Vendor
                entries.sort((a, b) => {
                  const sizeA = extractNumericValue(a.sizeKey);
                  const sizeB = extractNumericValue(b.sizeKey);
                  if (sizeA !== sizeB) return sizeA - sizeB;
                  
                  const weightA = extractNumericValue(a.weightKey);
                  const weightB = extractNumericValue(b.weightKey);
                  if (weightA !== weightB) return weightA - weightB;
                  
                  return a.vendorName.localeCompare(b.vendorName);
                });
                
                return (
                  <div key={productName} style={{ marginBottom: '20px' }}>
                    <h3 style={{ backgroundColor: '#e8f5e9', padding: '6px 10px', marginBottom: '5px', fontWeight: 'normal' }}>
                      {productName}
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Size</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Weight (kg)</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Vendor</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Qty</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Rate</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, idx, arr) => {
                          // Check if we should show the size (first occurrence)
                          const showSize = idx === 0 || arr[idx-1].sizeKey !== entry.sizeKey;
                          const sizeSpan = entries.filter(e => e.sizeKey === entry.sizeKey).length;
                          
                          // Check if we should show the weight (first occurrence within same size)
                          const showWeight = idx === 0 || 
                            arr[idx-1].sizeKey !== entry.sizeKey || 
                            arr[idx-1].weightKey !== entry.weightKey;
                          const weightSpan = entries.filter(e => 
                            e.sizeKey === entry.sizeKey && 
                            e.weightKey === entry.weightKey
                          ).length;
                          
                          return (
                            <tr key={`${entry.sizeKey}-${entry.weightKey}-${entry.vendorName}`}>
                              {showSize && (
                                <td rowSpan={sizeSpan} style={{ border: '1px solid #ddd', padding: '6px', fontWeight: 'bold' }}>
                                  {entry.sizeKey}
                                </td>
                              )}
                              {showWeight && (
                                <td rowSpan={weightSpan} style={{ border: '1px solid #ddd', padding: '6px' }}>
                                  {entry.weightKey}
                                </td>
                              )}
                              <td style={{ border: '1px solid #ddd', padding: '6px' }}>{entry.vendorName}</td>
                              {/* Show PIECES instead of total weight */}
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                {formatQuantity(entry.pieces)}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                {formatCurrency(entry.vendorData.rate)}
                              </td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
                                {formatCurrency(entry.vendorData.totalPrice)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
  <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
    <td colSpan={3} style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      Total for {productName}:
    </td>
    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      {formatQuantity(entries.reduce((sum, e) => sum + e.pieces, 0))}
    </td>
    <td style={{ border: '1px solid #ddd', padding: '6px' }}></td>
    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      {formatCurrency(
        entries.reduce((sum, e) => sum + (parseFloat(e.vendorData.totalPrice) || 0), 0)
      )}
    </td>
  </tr>
</tfoot>
                    </table>
                  </div>
                );
              })
            )}
            
            {/* ========== ROLL ========== */}
            {isRollCat && (
              Object.entries(categoryData).map(([productName, productData]) => {
                // Sort by size
                const sortedSizes = Object.entries(productData).sort((a, b) => {
                  return extractNumericValue(a[0]) - extractNumericValue(b[0]);
                });
                
                return (
                  <div key={productName} style={{ marginBottom: '20px' }}>
                    <h3 style={{ backgroundColor: '#e8f5e9', padding: '6px 10px', marginBottom: '5px', fontWeight: 'normal' }}>
                      {productName}
                    </h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f2f2f2' }}>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Size</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Quantity</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Rate</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSizes.map(([sizeKey, sizeData]) => {
                          let quantity = 0;
                          let rate = 0;
                          let totalValue = 0;
                          
                          if (sizeData.quantity !== undefined) {
                            quantity = parseFloat(sizeData.quantity) || 0;
                            rate = parseFloat(sizeData.rate) || 0;
                            totalValue = parseFloat(sizeData.totalPrice) || 0;
                          } else {
                            Object.values(sizeData).forEach(vendorData => {
                              quantity += parseFloat(vendorData.quantity) || 0;
                              totalValue += parseFloat(vendorData.totalPrice) || 0;
                              rate = parseFloat(vendorData.rate) || 0;
                            });
                          }
                          
                          return (
                            <tr key={sizeKey}>
                              <td style={{ border: '1px solid #ddd', padding: '6px' }}>{sizeKey}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatQuantity(quantity)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(rate)}</td>
                              <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(totalValue)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
  <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
    <td colSpan={3} style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      Total for {productName}:
    </td>
    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      {formatCurrency(
        sortedSizes.reduce((sum, [, sizeData]) => {
          if (sizeData.totalPrice !== undefined) {
            return sum + (parseFloat(sizeData.totalPrice) || 0);
          }
          return sum + Object.values(sizeData).reduce(
            (s, v) => s + (parseFloat(v.totalPrice) || 0), 0
          );
        }, 0)
      )}
    </td>
  </tr>
</tfoot>
                    </table>
                  </div>
                );
              })
            )}
            
            {/* ========== OTHER CATEGORIES ========== */}
            {!isReelTota && !isRollCat && !isPacketCat && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f2f2f2' }}>
                    <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>Product Name</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Quantity</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Rate</th>
                    <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>Total Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(categoryData).map(([productName, productData]) => (
                    <tr key={productName}>
                      <td style={{ border: '1px solid #ddd', padding: '6px' }}>{productName}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatQuantity(productData.quantity)}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(productData.rate)}</td>
                      <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatCurrency(productData.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
  <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
    <td colSpan={3} style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      Total:
    </td>
    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>
      {formatCurrency(
        Object.values(categoryData).reduce(
          (sum, p) => sum + (parseFloat(p.totalPrice) || 0), 0
        )
      )}
    </td>
  </tr>
</tfoot>
              </table>
            )}
          </div>
        );
      })}
      
      <div style={{ marginTop: '30px', padding: '10px', backgroundColor: '#f0f8ff', textAlign: 'right', fontWeight: 'bold' }}>
        <span>GRAND TOTAL: {formatQuantity(grandTotalQuantity)} units | {formatCurrency(grandTotalValue)}</span>
      </div>
    </div>
  );
};

export default InventoryPrint;